import React from 'react';
import { Copy, Link2, Mail, MessageSquare, Share2, Twitter, X } from 'lucide-react';
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { buildSocialShareLinks, resolveShareText } from '@/utils/share';

type ShareChannel = {
  key: string;
  label: string;
  href?: string;
  onClick?: () => void;
  bg: string;
  fg?: string;
  icon: React.ReactNode;
};

export const SharePopup: React.FC<{ open: boolean; url: string; text?: string; onClose: () => void }> = ({
  open,
  url,
  text,
  onClose,
}) => {
  const [copied, setCopied] = React.useState<'link' | 'message' | null>(null);

  React.useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  if (!open) return null;

  const fullMessage = resolveShareText(url, text);
  const links = buildSocialShareLinks({ url, text: text || fullMessage });

  const copy = async (value: string, kind: 'link' | 'message') => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement('input');
        el.value = value;
        document.body.appendChild(el);
        el.select();
        el.setSelectionRange(0, value.length);
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(kind);
      (window as any).toast?.('Copied');
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      try { prompt('Copy this text:', value); } catch {}
    }
  };

  const openHref = (href: string) => {
    try { window.open(href, '_blank', 'noopener,noreferrer'); } catch {}
  };

  const nativeShare = async () => {
    try {
      if (!(navigator as any)?.share) return;
      await (navigator as any).share({
        title: 'Digital Business Card',
        text: text || fullMessage.replace(url, '').trim(),
        url,
      });
      onClose();
    } catch {}
  };

  const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent || '');

  const channels: ShareChannel[] = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      href: links.whatsapp,
      bg: '#25D366',
      icon: <MessageSquare className="w-5 h-5" aria-hidden />,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      href: links.facebook,
      bg: '#1877F2',
      icon: <UnifiedBrandIcon platform="facebook" kind="social" size={20} color="#ffffff" />,
    },
    {
      key: 'twitter',
      label: 'X',
      href: links.twitter,
      bg: '#111827',
      icon: <Twitter className="w-5 h-5" aria-hidden />,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      href: links.linkedin,
      bg: '#0A66C2',
      icon: <UnifiedBrandIcon platform="linkedin" kind="social" size={20} color="#ffffff" />,
    },
    {
      key: 'telegram',
      label: 'Telegram',
      href: links.telegram,
      bg: '#26A5E4',
      icon: <Share2 className="w-5 h-5" aria-hidden />,
    },
    {
      key: 'email',
      label: 'Email',
      href: links.email,
      bg: '#64748B',
      icon: <Mail className="w-5 h-5" aria-hidden />,
    },
    ...(isMobile
      ? [{
          key: 'sms',
          label: 'SMS',
          href: links.sms,
          bg: '#059669',
          icon: <MessageSquare className="w-5 h-5" aria-hidden />,
        }]
      : []),
    {
      key: 'copy-link',
      label: copied === 'link' ? 'Copied!' : 'Copy link',
      onClick: () => { void copy(url, 'link'); },
      bg: '#2563EB',
      icon: <Link2 className="w-5 h-5" aria-hidden />,
    },
    {
      key: 'copy-message',
      label: copied === 'message' ? 'Copied!' : 'Copy text',
      onClick: () => { void copy(fullMessage, 'message'); },
      bg: '#7C3AED',
      icon: <Copy className="w-5 h-5" aria-hidden />,
    },
  ];

  const stop = (e: React.MouseEvent) => { e.stopPropagation(); };

  return (
    <div className="fixed inset-0 z-[60]" onClick={onClose} aria-modal role="dialog" aria-labelledby="share-sheet-title">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={stop}>
        <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <div id="share-sheet-title" className="font-semibold text-gray-900 text-lg">Share this card</div>
              <div className="text-xs text-gray-500 mt-0.5">Send to social media or copy the link</div>
            </div>
            <button className="p-2 rounded-full hover:bg-gray-100 text-gray-500" onClick={onClose} aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-4">
            {(navigator as any)?.share && (
              <button
                type="button"
                onClick={() => { void nativeShare(); }}
                className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share via device
              </button>
            )}

            <div className="grid grid-cols-4 gap-3">
              {channels.map((ch) => (
                <button
                  key={ch.key}
                  type="button"
                  className="flex flex-col items-center gap-2 group"
                  onClick={() => {
                    if (ch.onClick) {
                      ch.onClick();
                      return;
                    }
                    if (ch.href) openHref(ch.href);
                  }}
                >
                  <span
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform"
                    style={{ backgroundColor: ch.bg, color: ch.fg || '#fff' }}
                  >
                    {ch.icon}
                  </span>
                  <span className="text-[11px] font-medium text-gray-700 text-center leading-tight">{ch.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="px-5 pb-5">
            <label className="text-xs font-medium text-gray-500 mb-2 block">Card link</label>
            <div className="flex items-stretch gap-2">
              <input
                value={url}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-gray-50 text-gray-800"
              />
              <button
                type="button"
                className="px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
                onClick={() => { void copy(url, 'link'); }}
              >
                {copied === 'link' ? 'Copied' : 'Copy'}
              </button>
            </div>
            {text ? (
              <p className="mt-3 text-xs text-gray-500 leading-relaxed line-clamp-2">{fullMessage}</p>
            ) : (
              <p className="mt-3 text-xs text-gray-500">Tip: Copy the link or pick a platform above.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePopup;
